'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';
import Image from 'next/image';

import Container from '@src/components/Shared/Container';

import usePreviewContent from '@src/hooks/usePreviewContent';
import constructURL from '@src/utils/constructURL';

import ArrowForwardThin from '@src/components/Icons/ArrowForwardThin';

import DynamicStrengthIndexCard from './DynamicStrengthIndexCard/DynamicStrengthIndexCard';
import EstimatedOneRepMaxCard from './EstimatedOneRepMaxCard/EstimatedOneRepMaxCard';
import EccentricUtilizationRatioCard from './EccentricUtilizationRatioCard/EccentricUtilizationRatioCard';
import ReturnOnInvestmentCard from './ReturnOnInvestmentCard/ReturnOnInvestmentCard';
import JointRatioCard from './JointRatioCard/JointRatioCard';
import ForceRateRatioCard from './ForceRateRatioCard/ForceRateRatioCard';
import TorqueCard from './TorqueCard/TorqueCard';
import SpeedCard from './SpeedCard/SpeedCard';
import ImpactIndexCard from './ImpactIndexCard/ImpactIndexCard';
import AsymmetryCard from './AsymmetryCard/AsymmetryCard';
import RelativeStrengthCard from './RelativeStrengthCard/RelativeStrengthCard';
import LoadConversion from './LoadConversion/LoadConversion';
import DistanceConversion from './DistanceConversion/DistanceConversion';

import { CalculatorsPageClientPropTypes } from './CalculatorsPage.types';
import NextImage from '@src/components/Shared/NextImage';
import NextLink from '@src/components/Shared/NextLink';

const CalculatorsPageClient: React.FC<CalculatorsPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({
      languageCode: page?.languageCode?.toLowerCase(),
      slug: `/calculators`,
      baseURL: '',
    })
  );
  setContentType('site-page');

  return (
    <>
      <Container
        HtmlTag='header'
        className='text-body-copy--light 3xl:h-[80rem] flex h-[48.6rem] flex-col justify-center md:h-[50rem] lg:h-[75rem]'
        background="bg-[url('https://images.ctfassets.net/98s79sqwuajy/6GiQgzuKPTICbzFYKoXPlV/32c942b6654533ae4ce4a6e4c33e596d/msk-calculator-mobile.jpg')] lg:bg-[url('https://images.ctfassets.net/98s79sqwuajy/603GxVHgFG3smgHaMiFDTE/21ecd6621efd58153802e760dbbf16f5/msk-calculator-hero-image.png')] bg-no-repeat bg-position-[50%_80%] bg-cover 3xl:bg-position-[10%_55%]"
      >
        <h1 className='text-body-copy--light mb-[4rem] text-[6.4rem] leading-[6.4rem] font-medium tracking-[-0.16rem] lg:mb-[5.4rem] lg:text-[8.6rem] lg:leading-[7.7rem]'>
          MSK <br /> Calculators
        </h1>

        <div className='text-[1.4rem] md:text-[1.8rem] lg:text-[2.2rem] [&>p:not(:last-child)]:mb-[1rem]'>
          <p className='flex items-center leading-[1.2]'>
            <span className='mr-[1rem] font-semibold'>+</span> Calculate common musculoskeletal
            metrics and indexes.
          </p>
          <p className='flex items-center leading-[1.2]'>
            <span className='mr-[1rem] font-semibold'>÷</span> Compare values to evidence-based
            benchmarks.
          </p>
          <p className='flex items-center leading-[1.2]'>
            <span className='mr-[1rem] font-semibold'>×</span> Determine asymmetries and ratios from
            isolated tests.
          </p>
          <p className='flex items-center leading-[1.2] font-medium'>
            <span className='mr-[1rem] font-semibold'>=</span> Simple tools for calculating key
            movement metrics.
          </p>
        </div>

        <Image
          className='3xl:right-[0] absolute bottom-[-30%] hidden lg:right-[10%] lg:block lg:w-[30rem] xl:right-[5%] xl:w-[37.5rem]'
          alt='MSK Calculator'
          src='https://images.ctfassets.net/98s79sqwuajy/3yCEcTxfTHp4ykcmNhpX72/774c576281a16847350eb7758cdaeeba/msk-calculator.png'
          width={796}
          height={1298}
        />
      </Container>

      <Container className='text-body-copy--light lg:text-primary mt-[-15rem] flex flex-col items-center justify-center gap-[1.3rem] text-[1.5rem] font-bold lg:mt-[0] lg:text-[2.4rem] lg:font-normal'>
        <span>Explore Calculators</span>
        <ArrowForwardThin className='text-body-copy--light lg:text-primary w-[2.4rem]' />
      </Container>

      <Container className='mt-[-4rem] flex flex-col justify-center gap-[2.5rem] xl:mt-[0] xl:flex-row xl:gap-[5.4rem]'>
        <div className='flex flex-col gap-[2.5rem] xl:gap-[5.4rem] [&>div]:mx-auto'>
          <EstimatedOneRepMaxCard />
          <DynamicStrengthIndexCard />
          <ReturnOnInvestmentCard />
          <ForceRateRatioCard />
          <SpeedCard />
          <ImpactIndexCard />
        </div>
        <div className='flex flex-col gap-[2.5rem] xl:mt-[10.36rem] xl:gap-[5.4rem] [&>div]:mx-auto'>
          <LoadConversion />
          <EccentricUtilizationRatioCard />
          <JointRatioCard />
          <TorqueCard />
          <AsymmetryCard />
          <RelativeStrengthCard />
          <DistanceConversion />
        </div>
      </Container>

      <Container className='md:lg-[0] bg-background-tertiary 3xl:px-[4rem] 4xl:px-[0] relative z-1 mx-auto mb-[8rem] max-w-[118.4rem]! rounded-[2.2rem] px-[2.4rem] py-[4rem] text-center md:px-[6.3rem] md:pt-[5.1rem] md:pb-[3.5rem] lg:px-[6.4rem] xl:px-[12.8rem]'>
        <header className='flex flex-col items-start justify-center text-left md:items-center md:justify-center md:text-center'>
          <h2 className='text-body-copy--dark mb-[3.2rem] text-[2.4rem] leading-[3.2rem] font-light text-pretty md:text-[2.8rem] lg:mb-[5.6rem] lg:text-[4.8rem]'>
            Learn <span className='text-primary font-medium'>More</span>
          </h2>
        </header>
        <div className='mx-auto flex max-w-[107rem] flex-col justify-center gap-[2.4rem] md:flex-row'>
          <NextLink
            className='shadow-prime overflow-hidden rounded-[0.8rem]'
            href='https://valdperformance.com/news/understanding-the-plyometric-continuum-with-forcedecks'
          >
            <NextImage
              src={
                'https://images.ctfassets.net/98s79sqwuajy/4lJYW0EKKjJDHmbYIWd2Pj/913ff77fad287919f175b481e36c8e0c/VP_understanding_Plyometric_banner.jpg'
              }
              alt={'Understanding the Plyometric Continuum with ForceDecks'}
              aspectRatio={1320 / 670}
              width={1320}
            />
          </NextLink>

          <NextLink
            className='shadow-prime overflow-hidden rounded-[0.8rem]'
            href='https://valdperformance.com/news/research-summary-what-is-the-dynamic-strength-index'
          >
            <NextImage
              src={
                'https://images.ctfassets.net/98s79sqwuajy/4IDW2OtI60EocZHoQPbGwx/c8b7adc269d8ac2b3b0d1b1f1f2305ac/VP_Blog_Research_sum_dynamic_banner.jpg'
              }
              alt={'What is dynamic strength index'}
              aspectRatio={1320 / 670}
              width={1320}
            />
          </NextLink>
        </div>
      </Container>
    </>
  );
};

export default CalculatorsPageClient;
